import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { LoaderCircle } from 'lucide-react';
import { useStrings } from '@/contexts/strings-context';

interface ConfirmationModalProps {
  open: boolean;
  onGoBack: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
}

export default function ConfirmationModal({ open, onGoBack, onConfirm, isSubmitting = false }: ConfirmationModalProps) {
  const strings = useStrings();
  return (
    <AlertDialogPrimitive.Root open={open}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className="fixed inset-0 z-51 bg-black/70 backdrop-blur-[2px]" />

        <AlertDialogPrimitive.Content className="fixed left-1/2 top-1/2 z-[51] max-h-[calc(100dvh_-_32px)] w-[calc(100%_-_32px)] max-w-[540px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto overscroll-contain border border-border bg-background px-[22px] py-[26px] shadow-[0_18px_50px_rgba(10,10,10,0.22)] outline-none sm:px-[38px] sm:py-[34px]">
          <header className="text-center">
            <AlertDialogPrimitive.Title className="mt-[8px] text-[28px] font-bold leading-[33px] sm:text-[32px] sm:leading-[37px]">
              {/* Good to go? */}
              {strings.FINAL_TITLE}
            </AlertDialogPrimitive.Title>
          </header>

          <div className="my-[22px] border-t border-dashed border-[#e5e5e5]" />

          <AlertDialogPrimitive.Description asChild>
            <div
              className="mx-auto max-w-[455px] space-y-[16px] text-center [font-family:archivo_narrowregular,sans-serif] text-[16px] font-normal leading-[21px] text-muted-foreground sm:text-[18px] sm:leading-[23px]"
              dangerouslySetInnerHTML={{ __html: strings.FINAL_CONTENT }}
            ></div>
          </AlertDialogPrimitive.Description>

          <div className="mt-[28px] flex flex-col gap-[12px] sm:flex-row sm:items-center sm:justify-between sm:gap-[20px]">
            <AlertDialogPrimitive.Cancel asChild>
              <button
                className="button-screwfix-secondary flex h-[48px] w-full items-center justify-center !px-[22px] [font-family:archivo_narrowbold,sans-serif] !text-[clamp(18px,calc(16px_+_0.3vw),19px)] font-bold focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30 disabled:pointer-events-none disabled:opacity-60 sm:min-w-[150px] sm:w-auto"
                disabled={isSubmitting}
                onClick={onGoBack}
                type="button"
              >
                Go back
              </button>
            </AlertDialogPrimitive.Cancel>

            <AlertDialogPrimitive.Action asChild>
              <button
                aria-busy={isSubmitting}
                className="button-screwfix-success flex h-[48px] w-full items-center justify-center gap-[9px] !px-[22px] [font-family:archivo_narrowbold,sans-serif] !text-[clamp(18px,calc(16px_+_0.3vw),19px)] font-bold focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35 disabled:pointer-events-none disabled:opacity-60 sm:min-w-[190px] sm:w-auto"
                disabled={isSubmitting}
                onClick={onConfirm}
                type="button"
              >
                {isSubmitting ? <LoaderCircle aria-hidden="true" className="h-[18px] w-[18px] animate-spin" /> : null}
                {isSubmitting ? 'Submitting…' : strings.FINAL_BUTTON}
              </button>
            </AlertDialogPrimitive.Action>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
